/**
 * A minimal on-screen QWERTY keyboard with a number row. Most kiosk enclosures have a
 * touchscreen and nothing else, and `inputmode="none"` on the search
 * fields (see index.html) stops mobile browsers from also popping their
 * own OS keyboard and fighting this one for screen space.
 */
// Number row first — visitors often search by stand number (e.g. "2350").
const ROWS = ["1234567890", "qwertyuiop", "asdfghjkl", "zxcvbnm"];

export class OnScreenKeyboard {
  private el: HTMLElement;
  private target: HTMLInputElement | null = null;
  private onInput: (() => void) | null = null;

  constructor(container: HTMLElement) {
    this.el = container;
    this.render();
  }

  attach(target: HTMLInputElement, onInput: () => void): void {
    this.target = target;
    this.onInput = onInput;
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
    this.target = null;
    this.onInput = null;
  }

  private render(): void {
    this.el.innerHTML = "";
    this.el.setAttribute("role", "group");
    this.el.setAttribute("aria-label", "On-screen keyboard");

    for (const row of ROWS) {
      const rowEl = document.createElement("div");
      rowEl.className = "kb-row";
      for (const letter of row) {
        rowEl.appendChild(this.makeKey(letter, () => this.type(letter)));
      }
      this.el.appendChild(rowEl);
    }

    const bottomRow = document.createElement("div");
    bottomRow.className = "kb-row";
    bottomRow.appendChild(this.makeKey("space", () => this.type(" "), "kb-space"));
    bottomRow.appendChild(this.makeKey("⌫", () => this.backspace(), "kb-back"));
    this.el.appendChild(bottomRow);
  }

  private makeKey(label: string, onTap: () => void, extraClass = ""): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `kb-key ${extraClass}`.trim();
    btn.textContent = label;
    // pointerdown fires before the input loses focus on most browsers,
    // and preventDefault stops that focus loss entirely so the cursor
    // never leaves the field between key taps.
    btn.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      onTap();
    });
    return btn;
  }

  private type(char: string): void {
    if (!this.target) return;
    this.target.value += char;
    this.target.dispatchEvent(new Event("input", { bubbles: true }));
    this.onInput?.();
  }

  private backspace(): void {
    if (!this.target) return;
    this.target.value = this.target.value.slice(0, -1);
    this.target.dispatchEvent(new Event("input", { bubbles: true }));
    this.onInput?.();
  }
}
